<template>
    <div
        class="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer group relative overflow-hidden"
        @click="handleIndustryClick"
    >


        <div class="relative z-10 flex flex-col h-full">
            <div class="flex items-start justify-between mb-4">
                <div class="flex items-center gap-3">
                    <div class="text-2xl transition-transform duration-200">
                        {{ industry.emoji || '🏭' }}
                    </div>
                    <h3 class="text-lg font-semibold text-slate-900">
                        {{ industry.label }}
                    </h3>
                </div>
                <div v-if="industry.categories && industry.categories.length > 0" class="flex items-center gap-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 px-2.5 py-1 rounded-md border border-indigo-100">
                    <AppIcon name="layer-group" class="h-3 w-3" />
                    <span class="text-xs font-semibold">{{ industry.categories.length }}</span>
                </div>
            </div>

            <div v-if="industry.categories && industry.categories.length > 0" class="space-y-3">
                <!-- Show first 3 categories as bubble tags -->
                <div class="flex flex-wrap gap-2">
                    <div
                        v-for="category in industry.visibleCategories"
                        :key="category.id"
                        class="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-[background-color,border-color] border bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 cursor-pointer"
                        @click.stop="handleCategoryClick(category)"
                    >
                        <span v-if="category.emoji || category.icon" class="mr-1.5">{{ category.emoji || category.icon }}</span>
                        {{ category.label }}
                    </div>
                </div>

                <!-- Show "expand" button when categories are collapsed -->
                <div
                    v-if="industry.hiddenCategoriesCount > 0 && !showAll"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 cursor-pointer transition-colors duration-200"
                    @click.stop="toggleShowAll"
                >
                    <AppIcon
                        name="chevron-down"
                        class="h-3 w-3"
                    />
                    <span>{{ industry.hiddenCategoriesCount }} more</span>
                </div>

                <!-- Show additional categories when expanded -->
                <div v-if="showAll" class="pt-0 mt-0">
                    <div class="flex flex-wrap gap-2 mb-3">
                        <div
                            v-for="category in industry.categories.slice(3)"
                            :key="category.id"
                            class="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-[background-color,border-color] border bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200 cursor-pointer"
                            @click.stop="handleCategoryClick(category)"
                        >
                            <span v-if="category.emoji || category.icon" class="mr-1.5">{{ category.emoji || category.icon }}</span>
                            {{ category.label }}
                        </div>
                    </div>

                    <!-- Show "collapse" button at the bottom when expanded -->
                    <div
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 cursor-pointer transition-colors duration-200"
                        @click.stop="toggleShowAll"
                    >
                        <AppIcon
                            name="chevron-up"
                            class="h-3 w-3"
                        />
                        <span>Show less</span>
                    </div>
                </div>
            </div>

            <div v-else class="text-sm text-slate-400 italic">
                No categories available
            </div>

            <!-- Footer action -->
            <div class="mt-auto pt-3 border-t border-slate-100 flex items-center justify-end">
                <div class="text-xs font-medium text-indigo-600 flex items-center gap-1">
                    <span>View creators</span>
                    <AppIcon name="arrow-right" class="h-3 w-3" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'

const props = defineProps({
    industry: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['industry-click', 'category-click'])

const showAll = ref(false)

const handleIndustryClick = () => {
    emit('industry-click', props.industry)
}

const handleCategoryClick = (category) => {
    emit('category-click', props.industry, category)
}

const toggleShowAll = () => {
    showAll.value = !showAll.value
}
</script>